import './TablesTonight.scss';
import { useCallback, useEffect, useState } from 'react';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import Skeleton from '../../components/Skeleton/Skeleton';

// TablesTonight: what is still bookable across every venue we collect.
// Source: GET /admin/table-availability/tonight?date=YYYY-MM-DD
//
// Two things this page must not imply:
//   - UrVenue venues report no capacity, only what is still free, so their
//     "of N" is deliberately absent rather than filled with a guess.
//   - A venue missing from the list has no event that night, which is not the
//     same as a venue whose feed broke. The watchdog owns that second case.

// A night belongs to the day it started on, so 1am Monday still means Sunday's
// party. Without the 6am cutoff the page jumped to the next day at midnight and
// dropped every venue still open, which is most of them.
//
// Reads the Miami wall-clock hour rather than subtracting six hours of absolute
// time: a fixed offset rolls the night at 5am on fall-back day and 7am on
// spring-forward day, which would disagree with the server's night key on the
// four hours a year the venues are still open. Mirrors src/utils/nightKey.js.
const miamiTonight = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    hour12: false,
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const ymd = `${get('year')}-${get('month')}-${get('day')}`;
  if (Number(get('hour')) % 24 >= 6) return ymd;
  const prev = new Date(`${ymd}T12:00:00Z`);
  prev.setUTCDate(prev.getUTCDate() - 1);
  return prev.toISOString().slice(0, 10);
};

const prettyDate = (ymd) => {
  if (!ymd) return '';
  const d = new Date(`${ymd}T12:00:00`);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const sinceLabel = (ts) => {
  if (!ts) return 'never';
  const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 48 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
};

const money = (n) => (typeof n === 'number' && n > 0 ? `$${n.toLocaleString()}` : '');

const TablesTonight = () => {
  const [date, setDate] = useState(miamiTonight());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (ymd) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/table-availability/tonight?date=${ymd}`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not load availability');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const venues = data?.venues || [];
  const totals = venues.reduce((acc, v) => ({
    remaining: acc.remaining + v.totals.remaining,
    soldOut: acc.soldOut + v.totals.sold_out_tiers,
    moved: acc.moved + v.totals.moved_today,
  }), { remaining: 0, soldOut: 0, moved: 0 });

  const shiftDay = (delta) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setDate(d.toLocaleDateString('en-CA'));
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="tables-tonight">

          <div className="tables-tonight__head">
            <div>
              <h1>Tables tonight</h1>
              <p className="tables-tonight__sub">
                What is still bookable at every venue we collect. Venues with no event that
                night are not listed.
              </p>
            </div>
            <div className="tables-tonight__datenav">
              <button onClick={() => shiftDay(-1)} aria-label="Previous night">‹</button>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              <button onClick={() => shiftDay(1)} aria-label="Next night">›</button>
              <button className="is-today" onClick={() => setDate(miamiTonight())}>Tonight</button>
            </div>
          </div>

          {loading && <Skeleton />}
          {error && <div className="tables-tonight__error">{error}</div>}

          {!loading && !error && (
            <>
              <div className="tables-tonight__summary">
                <div className="stat">
                  <span className="stat__n">{venues.length}</span>
                  <span className="stat__l">venues with an event</span>
                </div>
                <div className="stat">
                  <span className="stat__n">{totals.remaining}</span>
                  <span className="stat__l">tables still bookable</span>
                </div>
                <div className={`stat${totals.soldOut ? ' stat--alert' : ''}`}>
                  <span className="stat__n">{totals.soldOut}</span>
                  <span className="stat__l">sold-out table types</span>
                </div>
                <div className="stat">
                  <span className="stat__n">{totals.moved}</span>
                  <span className="stat__l">taken since we started watching</span>
                </div>
              </div>

              {!venues.length && (
                <div className="tables-tonight__empty">
                  No venue we collect has an event on {prettyDate(date)}.
                </div>
              )}

              <div className="tables-tonight__grid">
                {venues.map((v) => (
                  <section className="vcard" key={v.venue_id}>
                    <header className="vcard__head">
                      <div>
                        <h2>{v.venue_name}</h2>
                        <span className="vcard__meta">
                          {v.source} · read {sinceLabel(v.observed_at)} · {v.readings} reading{v.readings === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className="vcard__count">
                        <strong>{v.totals.remaining}</strong>
                        {v.totals.total !== null && <span>of {v.totals.total}</span>}
                        {v.totals.total === null && <span className="vcard__nocap">free</span>}
                      </div>
                    </header>

                    <ul className="vcard__tiers">
                      {v.tiers.map((t) => (
                        <li
                          key={t.tier_name}
                          className={t.sold_out ? 'is-out' : t.remaining <= 1 && t.remaining !== t.total ? 'is-low' : ''}
                        >
                          <span className="t__name" title={t.tier_name}>{t.tier_name}</span>
                          <span className="t__price">{money(t.price)}</span>
                          <span className="t__rem">
                            {t.sold_out ? 'sold out' : t.total !== null ? `${t.remaining} of ${t.total}` : `${t.remaining} free`}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {v.totals.total === null && (
                      <p className="vcard__note">
                        This venue publishes what is free, not how many exist, so there is no
                        capacity to compare against.
                      </p>
                    )}
                  </section>
                ))}
              </div>
            </>
          )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default TablesTonight;
